import type { ContinuousProgress, GamePreferences } from "../gameplay/ProgressManager";
import type { PwaManager } from "../pwa/PwaManager";
import { initializeSettingsControls, settingsControlsMarkup } from "./SettingsControls";
import { DEFAULT_DIFFICULTY_ID, sanitizeDifficultyId, type DifficultyId } from "../gameplay/Difficulty";
import { bindDifficultyOptions, difficultyOptionsMarkup } from "./DifficultyControls";
import { FeedbackForm } from "./FeedbackForm";

export interface MainMenuActions {
  onStart: (difficultyId: DifficultyId) => void;
  onPreferences: (preferences: GamePreferences) => void;
  onCredits: () => void;
  onInstall: () => void;
}

export class MainMenu {
  private feedback?: FeedbackForm;
  constructor(
    private readonly root: HTMLElement,
    private readonly progress: ContinuousProgress,
    private preferences: GamePreferences,
    private readonly actions: MainMenuActions,
    private readonly pwa: Pick<PwaManager,"canInstall"|"shouldShowIosInstallHelp">,
  ) {}

  show(): void {
    this.feedback?.dispose();
    this.feedback = undefined;
    this.root.innerHTML = `
      <main class="screen menu-screen">
        <section class="menu-stage" aria-label="Penangkap Hantu">
          <picture class="menu-art">
            <source media="(max-width: 760px) and (orientation: portrait)" srcset="./assets/Phone.png">
            <img src="./assets/Desktop.png" alt="Penangkap Hantu bersama Din Belut dan hantu-hantu Kampung Sanggang" draggable="false">
          </picture>
          <div class="menu-card">
            <nav class="menu-actions" aria-label="Menu utama">
              <button class="primary" data-action="start">MULA MAIN</button>
              <button data-action="how">CARA BERMAIN</button>
              <button data-action="settings">TETAPAN</button>
              <button data-action="credits">KREDIT</button>
              <button data-action="feedback">MAKLUM BALAS</button>
            </nav>
            <div class="pwa-install-area"></div>
          </div>
        </section>
      </main>`;
    this.bind();
    this.updateInstallExperience();
  }

  updateInstallExperience():void {
    const area=this.root.querySelector<HTMLElement>(".pwa-install-area");if(!area)return;
    if(this.pwa.canInstall){area.innerHTML='<button class="pwa-install-button">PASANG PERMAINAN</button>';area.querySelector("button")?.addEventListener("click",this.actions.onInstall,{once:true});return;}
    area.innerHTML=this.pwa.shouldShowIosInstallHelp?'<p>Untuk pasang permainan pada iPhone, buka menu Kongsi dan pilih <strong>Add to Home Screen</strong>.</p>':"";
  }

  private bind(): void {
    this.root.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => button.addEventListener("click", () => {
      const action = button.dataset.action;
      if (action === "start") this.showDifficultySelection();
      else if (action === "how") this.showHowTo();
      else if (action === "settings") this.showSettings();
      else if (action === "credits") this.actions.onCredits();
      else if (action === "feedback") this.showFeedback();
    }));
  }

  showDifficultySelection(): void {
    const initial = sanitizeDifficultyId(this.progress.difficultyId ?? DEFAULT_DIFFICULTY_ID);
    this.root.innerHTML = `<main class="screen sub-screen difficulty-screen"><section class="wide-card glass-panel difficulty-card"><button class="back">← Kembali</button><p class="eyebrow">KEMPEN BAHARU</p><h2>PILIH TAHAP KESUKARAN</h2><p class="difficulty-current">Pilihan semasa: <strong>${initial === "buang_tebiat" ? "BUANG TEBIAT" : initial.toUpperCase()}</strong></p>${difficultyOptionsMarkup(initial)}<button class="primary start-campaign">MULA KEMPEN</button></section></main>`;
    let selected = initial;
    bindDifficultyOptions(this.root, initial, (id) => {
      selected = id;
      const label = id === "buang_tebiat" ? "BUANG TEBIAT" : id.toUpperCase();
      const current = this.root.querySelector<HTMLElement>(".difficulty-current strong");
      if (current) current.textContent = label;
    });
    this.root.querySelector(".back")?.addEventListener("click", () => this.show());
    this.root.querySelector(".start-campaign")?.addEventListener("click", () => this.actions.onStart(selected));
    this.root.querySelector<HTMLButtonElement>(`[data-difficulty="${initial}"]`)?.focus();
  }

  private showHowTo(): void {
    this.root.innerHTML = `<main class="screen sub-screen"><section class="wide-card glass-panel instructions"><button class="back">← Kembali</button><p class="eyebrow">PANDUAN PEMBURU</p><h2>Cara Bermain</h2><div class="instruction-grid"><article><b>1</b><h3>Ikut Petunjuk</h3><p>Din Belut memburu lima hantu mengikut turutan: Toyol, Polong, Pocong, Penanggal dan akhirnya Pontianak. Hanya satu pertemuan aktif pada satu masa.</p></article><article><b>2</b><h3>Padankan Umpan</h3><p>Syiling menarik Toyol, Lilin menarik Polong, Trampoline menarik Pocong, Paket Darah Palsu menarik Penanggal dan Cermin menarik Pontianak. Umpan salah tidak mendedahkan hantu.</p></article><article><b>3</b><h3>Guna Senjata Betul</h3><p>Gunakan Lastik, Belon Air, Kulit Pisang, Penyembur Nyamuk dan Paku pada hantu sepadan. Pocong mesti jatuh empat kali; sembur Penanggal secara berterusan.</p></article><article><b>4</b><h3>Tangkap Ikut Masa</h3><p>Turunkan HP hantu ke kosong, bidik hantu itu dan gunakan Botol Kaca dalam 3–7 saat mengikut tahap kesukaran. Pontianak cuba melarikan diri apabila tinggal 1 HP.</p></article><article><b>5</b><h3>Jaga Din Belut</h3><p>Serangan hantu mengurangkan HP pemain. Jika Din Belut pengsan, cuba semula pertemuan semasa. Tangkap kelima-lima hantu untuk membuka Koleksi Hantu.</p></article></div><p class="controls-copy"><strong>Desktop:</strong> WASD bergerak · Tetikus melihat · Klik kiri guna/tembak · Tahan klik kiri untuk semburan · Klik kanan tukar senjata · Kekunci 1–9 memilih sembilan slot pertama · 0 memilih Paku · Space cuba tangkap terus dengan Botol · E ambil umpan<br><strong>Telefon:</strong> Sentuh kiri bergerak · Leret kanan melihat · LETAK untuk umpan · Tahan SERANG untuk semburan · TANGKAP untuk Botol dipilih · BOTOL kekal pintasan tangkap</p></section></main>`;
    this.root.querySelector(".back")?.addEventListener("click", () => this.show());
  }

  private showSettings(): void {
    this.root.innerHTML = `<main class="screen sub-screen"><section class="dialog-card settings-card glass-panel"><button class="back">← Kembali</button><p class="eyebrow">TETAPAN</p><h2>Suasana Permainan</h2>${settingsControlsMarkup(this.preferences)}<button class="primary save-settings">Simpan Tetapan</button></section></main>`;
    const read = initializeSettingsControls(this.root, this.preferences);
    this.root.querySelector(".back")?.addEventListener("click", () => this.show());
    this.root.querySelector(".save-settings")?.addEventListener("click", () => {
      this.preferences = read();
      this.actions.onPreferences(this.preferences); this.show();
    });
  }

  private showFeedback(): void {
    this.feedback?.dispose();
    this.feedback = new FeedbackForm(this.root, this.progress, () => this.show());
    this.feedback.show();
  }
}
